import { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useMutation } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime';

import { TrackSourceType } from '../../api/cc-smarts/models/TrackSourceType';
import type { TrackType } from '../../api/cc-smarts/models/TrackType';
import type { ActiveContent } from '../../types/activeContent';
import { type FlagsContainer, useFlags } from '../context/FlagsProviderContext';
import { openAudioPlayer } from '../state/AudioPlayerState';

import type {
	ConfluenceTrackSourceInput,
	useContextMenuActionsAddTrackMutation,
} from './__generated__/useContextMenuActionsAddTrackMutation.graphql';
import type { useContextMenuActionsRemoveTrackMutation } from './__generated__/useContextMenuActionsRemoveTrackMutation.graphql';
import type {
	ConfluenceTrackInput,
	useContextMenuActionsReorderTrackMutation,
} from './__generated__/useContextMenuActionsReorderTrackMutation.graphql';

const messages = defineMessages({
	reorderSuccessTitle: {
		id: 'ai-audio.playlist.reorder-success-description',
		defaultMessage: 'Moved {title}',
		description: 'Description shown when an item is reordered in the playlist',
	},
	reorderErrorDescription: {
		id: 'ai-audio.playlist.reorder-error-description',
		defaultMessage: 'Failed to move item in playlist',
		description: 'Description shown when an item fails to be reordered in the playlist',
	},
	removeSuccess: {
		id: 'ai-audio.playlist.remove-success',
		defaultMessage: 'Removed from playlist',
		description: 'Success message shown when an item is removed from the playlist',
	},
	errorTitle: {
		id: 'ai-audio.playlist.error',
		defaultMessage: 'Something went wrong',
		description: 'Error message shown when an error occurs.',
	},
	removeSuccessDescription: {
		id: 'ai-audio.playlist.remove-success-description',
		defaultMessage: '{title} has been removed from your playlist.',
		description: 'Description shown when an item is removed from the playlist',
	},
	removeSuccessEmptyDescription: {
		id: 'ai-audio.playlist.remove-success-empty-description',
		defaultMessage: '{title} has been removed from your playlist. Your playlist is now empty.',
		description:
			'Description shown when an item is removed from the playlist and the playlist is now mpty',
	},
	removeErrorDescription: {
		id: 'ai-audio.playlist.remove-error-description',
		defaultMessage: 'Failed to remove item from playlist.',
		description: 'Description shown when an item fails to be removed from the playlist',
	},
	addSuccess: {
		id: 'ai-audio.playlist.add-success',
		defaultMessage: 'Added to playlist',
		description: 'Success message shown when an item is added from the playlist',
	},
	addError: {
		id: 'ai-audio.playlist.add-error',
		defaultMessage: 'Something went wrong',
		description: 'Error message shown when an item fails to be added to the playlist',
	},
	addSuccessDescription: {
		id: 'ai-audio.playlist.add-success-description',
		defaultMessage:
			'{title} has been added to your playlist. Listen anywhere on Confluence or the mobile app.',
		description: 'Description shown when an item is added to the playlist',
	},
	addErrorDescription: {
		id: 'ai-audio.playlist.add-error-description',
		defaultMessage: 'Failed to add item to playlist.',
		description: 'Description shown when an item fails to be added to the playlist',
	},
	addErrorContentTooShort: {
		id: 'ai-audio.playlist.add-error-content-too-short',
		defaultMessage:
			'This content is too short to add to the playlist. Add more content and try again.',
		description: 'Error message shown when content is too short to be added to the playlist',
	},
	playAction: {
		id: 'ai-audio.playlist.add-success.play-action',
		defaultMessage: 'Play',
		description: 'Action button to play the added track',
	},
	openPlaylistAction: {
		id: 'ai-audio.playlist.add-success.open-playlist-action',
		defaultMessage: 'Open playlist',
		description: 'Action button to open the playlist',
	},
});

/**
 * Helper function to check if a track node matches the given track parameters
 */
const isMatchingTrack = (
	node: any,
	mainSource: string,
	trackType: any,
	supportingSources: readonly string[],
): boolean => {
	if (node?.getValue('mainSource') !== mainSource || node?.getValue('type') !== trackType) {
		return false;
	}
	const nodeSupportingSources = (node?.getValue('supportingSources') as string[]) || [];
	// Check that arrays have the same length first
	if (nodeSupportingSources.length !== supportingSources.length) {
		return false;
	}
	// Then check that all elements match
	return nodeSupportingSources.every(
		(source: string, index: number) => source === supportingSources[index],
	);
};

export const useContextMenuActions = (
	cloudId: string,
	flagsOverride?: FlagsContainer,
): {
	removeTrack: (
		mainSource: string,
		supportingSources: string[],
		trackType: TrackType,
		title?: string,
		options?: {
			showFlags?: boolean;
			playlist?: ActiveContent[];
		},
	) => Promise<boolean>;
	isRemoving: boolean;
	isDisabled: boolean;
	addTrack: (
		mainSource: string,
		supportingSources: string[] | undefined,
		trackType: TrackType,
		options?: {
			top?: boolean;
			showFlags?: boolean;
			trackSource?: ConfluenceTrackSourceInput;
		},
	) => Promise<boolean>;
	isAdding: boolean;
	reorderTrack: (
		targetTrack: ConfluenceTrackInput,
		referenceTrack?: ConfluenceTrackInput,
		title?: string,
	) => Promise<boolean>;
} => {
	const { formatMessage } = useIntl();
	const contextFlags = useFlags();
	const flags = flagsOverride ?? contextFlags;

	const [removeTrackMutation, isRemoveMutationInFlight] =
		useMutation<useContextMenuActionsRemoveTrackMutation>(graphql`
			mutation useContextMenuActionsRemoveTrackMutation(
				$cloudId: ID!
				$input: ConfluenceTrackInput!
			) {
				confluence_removeTrack(cloudId: $cloudId, input: $input) {
					success
					errors {
						message
						extensions {
							statusCode
						}
					}
				}
			}
		`);

	const [reorderTrackMutation, isReorderMutationInFlight] =
		useMutation<useContextMenuActionsReorderTrackMutation>(graphql`
			mutation useContextMenuActionsReorderTrackMutation(
				$cloudId: ID!
				$input: ConfluenceReorderTrackInput!
			) {
				confluence_reorderTracks(cloudId: $cloudId, input: $input) {
					success
					errors {
						message
						extensions {
							statusCode
						}
					}
				}
			}
		`);

	const removeTrack = useCallback(
		(
			mainSource: string,
			supportingSources: string[],
			trackType: TrackType,
			title?: string,
			options?: {
				showFlags?: boolean;
				playlist?: ActiveContent[];
			},
		): Promise<boolean> => {
			const showFlags = options?.showFlags ?? true;

			return new Promise<boolean>((resolve, reject) => {
				removeTrackMutation({
					variables: {
						cloudId,
						input: {
							mainSource,
							supportingSources,
							trackType,
						},
					},
					onCompleted: (response) => {
						const success = response.confluence_removeTrack?.success;
						const errors = response.confluence_removeTrack?.errors;
						const nextTrackThatIsNotBeingRemoved =
							options?.playlist && options.playlist.length > 0
								? //this is not perfect. This exists because if we remove the top item of the playlist the item the player will load will be the one we just removed.
									//however, since we can have a narration and a briefing (and because this function take a mainSource ari instead of a contentID)
									//this is the best check at my disposal
									options?.playlist.find((item) => item.title !== title)
								: undefined;

						const trackContent = {
							contentId: '',
							spaceKey: '',
							contentType: '',
							title: '',
							author: '',
							avatarUrl: '',
							mainContentLink: '',
							supportingContent: [],
							subType: '',
							emojiId: undefined,
						};

						const successActions = [
							{
								content: formatMessage(messages.openPlaylistAction),
								onClick: () => {
									if (nextTrackThatIsNotBeingRemoved) {
										openAudioPlayer(nextTrackThatIsNotBeingRemoved as ActiveContent, {
											expandPlaylist: true,
										});
									} else {
										openAudioPlayer(trackContent, {
											expandPlaylist: true,
										});
									}
								},
							},
						];

						if (success) {
							resolve(true);
							if (!flags || !title || !showFlags) {
								return;
							}

							// TODO: Make sure that if there is one item in the array, it is the one we just removed
							const isEmptyAfterRemoval: boolean =
								(options?.playlist && options.playlist.length <= 1) ?? false;

							flags.showFlag({
								type: 'success-circle',
								title: formatMessage(messages.removeSuccess),
								description: isEmptyAfterRemoval
									? formatMessage(messages.removeSuccessEmptyDescription, {
											title,
										})
									: formatMessage(messages.removeSuccessDescription, {
											title,
										}),
								close: 'auto',
								actions: isEmptyAfterRemoval ? [] : successActions,
							});
						} else {
							const errorMessage = errors?.[0]?.message || 'Failed to remove track';
							reject(new Error(errorMessage));

							if (flags && showFlags) {
								flags.showFlag({
									title: formatMessage(messages.errorTitle),
									description: formatMessage(messages.removeErrorDescription),
									type: 'error',
									close: 'auto',
								});
							}
						}
					},
					onError: (error) => {
						reject(error);
						if (flags && showFlags) {
							flags.showFlag({
								title: formatMessage(messages.errorTitle),
								description: formatMessage(messages.removeErrorDescription),
								type: 'error',
								close: 'auto',
							});
						}
					},
					updater: (store) => {
						// Update the Relay store to remove the track from the cache
						// This ensures the UI updates immediately without refetching
						const playlistRecord = store.getRoot().getLinkedRecord('confluence_getPlaylist', {
							cloudId,
						});
						if (playlistRecord) {
							const tracksRecord = playlistRecord.getLinkedRecord('tracks');
							if (tracksRecord) {
								const nodes = tracksRecord.getLinkedRecords('nodes');
								if (nodes) {
									const updatedNodes = nodes.filter((node) => {
										// If the nodes mainSource  is undefined, we should remove it.
										if (node.getValue('mainSource') === undefined) {
											return false;
										}
										return !isMatchingTrack(node, mainSource, trackType, supportingSources);
									});
									tracksRecord.setLinkedRecords(updatedNodes, 'nodes');
								}
							}
						}
					},
				});
			});
		},
		[removeTrackMutation, cloudId, flags, formatMessage],
	);

	const [addTrackMutation, isAddMutationInFlight] =
		useMutation<useContextMenuActionsAddTrackMutation>(graphql`
			mutation useContextMenuActionsAddTrackMutation(
				$cloudId: ID!
				$input: ConfluenceAddTrackInput!
			) {
				confluence_addTrack(cloudId: $cloudId, input: $input) {
					success
					errors {
						message
						extensions {
							statusCode
						}
					}
					track {
						id
						type
						mainSource
						trackSource
						supportingSources
						supportingContent {
							ari
							links {
								webui
								base
							}
						}
						mainContent {
							id
							title
							type
							status
							subType
							links {
								webui
								base
							}
							space {
								key
							}
							history {
								createdBy {
									displayName
									profilePicture {
										path
									}
								}
								ownedBy {
									displayName
									profilePicture {
										path
									}
								}
							}
							properties(keys: ["emoji-title-published"]) {
								nodes {
									value
								}
							}
						}
					}
				}
			}
		`);

	const addTrack = useCallback(
		(
			mainSource: string,
			supportingSources: string[] = [],
			trackType: TrackType,
			options?: {
				top?: boolean;
				showFlags?: boolean;
				trackSource?: ConfluenceTrackSourceInput;
			},
		): Promise<boolean> => {
			const top = options?.top ?? false;
			const showFlags = options?.showFlags ?? true;

			return new Promise<boolean>((resolve, reject) => {
				addTrackMutation({
					variables: {
						cloudId,
						input: {
							track: {
								mainSource,
								supportingSources,
								// This option allows us to persist the original track source when the track was first added to the playlist
								trackSource: options?.trackSource ?? TrackSourceType.WEB,
								trackType: trackType || 'BRIEFING',
							},
							...(top && { top: true }),
						},
					},
					onCompleted: (response: any) => {
						const success = response.confluence_addTrack?.success;
						const errors = response.confluence_addTrack?.errors;
						const title = response.confluence_addTrack?.track?.mainContent?.title;
						const track = response.confluence_addTrack?.track;
						const mainContent = track?.mainContent;

						if (success) {
							resolve(true);
							if (flags && title && showFlags) {
								const creator = mainContent.history?.createdBy || mainContent.history?.ownedBy;
								const trackContent = mainContent
									? {
											contentId: mainContent.id || '',
											spaceKey: mainContent.space?.key || '',
											contentType: mainContent.type || '',
											title: mainContent.title || '',
											author: creator?.displayName || '',
											avatarUrl: creator?.profilePicture?.path || '',
											mainContentLink: mainContent.links?.base + mainContent.links?.webui || '',
											supportingContent:
												track?.supportingContent?.map((content: any) => ({
													ari: content.ari,
													link: content.links?.base + content.links?.webui || '',
												})) || [],
											subType: mainContent.subType || '',
											emojiId: mainContent.properties?.nodes?.[0]?.value || undefined,
											trackSource: track?.trackSource || TrackSourceType.WEB,
										}
									: null;
								flags.showFlag({
									title: formatMessage(messages.addSuccess),
									description: formatMessage(messages.addSuccessDescription, { title }),
									type: 'success-circle',
									close: 'auto',
									actions: [
										{
											content: formatMessage(messages.playAction),
											onClick: () => {
												if (trackContent && track) {
													const trackMainSource = track.mainSource;
													const trackSupportingSources = track.supportingSources || [];
													const trackType = track.type;
													if (trackMainSource && trackType) {
														addTrack(trackMainSource, trackSupportingSources, trackType, {
															top: true,
															showFlags: false,
															// The flag action will always be on web
															trackSource: TrackSourceType.WEB,
														});
													}
													openAudioPlayer(trackContent, {
														playImmediately: true,
														expandPlaylist: false,
													});
												}
											},
										},
										{
											content: formatMessage(messages.openPlaylistAction),
											onClick: () => {
												// Use the new track whenever possible, otherwise fall back to current active content
												if (trackContent) {
													openAudioPlayer(trackContent, {
														expandPlaylist: true,
													});
												}
											},
										},
									],
								});
							}
						} else {
							const errorMessage = errors?.[0]?.message || 'Failed to add track';
							const isContentTooShort =
								errors?.[0]?.extensions?.statusCode === 400 &&
								errors?.[0]?.message === 'Context size is too small';

							let description = formatMessage(messages.addErrorDescription);
							if (isContentTooShort) {
								description = formatMessage(messages.addErrorContentTooShort);
							}

							reject(new Error(errorMessage));
							if (flags && showFlags) {
								flags.showFlag({
									title: formatMessage(messages.addError),
									description: description,
									type: 'error',
									close: 'auto',
								});
							}
						}
					},
					onError: () => {
						reject(new Error('Failed to add track'));
						if (flags && showFlags) {
							flags.showFlag({
								title: formatMessage(messages.addError),
								description: formatMessage(messages.addErrorDescription),
								type: 'error',
								close: 'auto',
							});
						}
					},
					updater: (store: RecordSourceSelectorProxy) => {
						// Update the Relay store to add/move the track in the playlist
						const payload = store.getRootField('confluence_addTrack');
						const newTrack = payload?.getLinkedRecord('track');
						if (!payload?.getValue('success') || !newTrack) {
							return;
						}

						const playlist = store.getRoot().getLinkedRecord('confluence_getPlaylist', {
							cloudId,
						});
						const tracks = playlist?.getLinkedRecord('tracks');
						if (!tracks) {
							return;
						}

						const nodes = tracks.getLinkedRecords('nodes') || [];

						// Remove existing track if present, then add the new track
						const filteredNodes = nodes.filter(
							(node) => !isMatchingTrack(node, mainSource, trackType, supportingSources),
						);
						const updatedNodes = top ? [newTrack, ...filteredNodes] : [...filteredNodes, newTrack];

						tracks.setLinkedRecords(updatedNodes, 'nodes');
					},
				});
			});
		},
		[cloudId, addTrackMutation, flags, formatMessage],
	);

	/**
	 * • Target track - The track being repositioned
	 * • Reference track - The track that serves as the anchor point. The target track will be placed immediately after
	 *    the reference track. If no reference track is specified, the target track moves to the beginning of the playlist.
	 */
	const reorderTrack = useCallback(
		(
			targetTrack: ConfluenceTrackInput,
			referenceTrack?: ConfluenceTrackInput,
			title?: string,
		): Promise<boolean> => {
			return new Promise<boolean>((resolve, reject) => {
				reorderTrackMutation({
					variables: {
						cloudId,
						input: {
							targetTrack,
							referenceTrack,
						},
					},
					updater: (store) => {
						const playlistRecord = store.getRoot().getLinkedRecord('confluence_getPlaylist', {
							cloudId,
						});

						if (!playlistRecord) {
							return;
						}

						const tracksRecord = playlistRecord.getLinkedRecord('tracks');
						if (!tracksRecord) {
							return;
						}

						const nodes = tracksRecord.getLinkedRecords('nodes');
						if (!nodes) {
							return;
						}

						const targetIndex = nodes.findIndex((node) =>
							isMatchingTrack(
								node,
								targetTrack.mainSource,
								targetTrack.trackType,
								targetTrack.supportingSources || [],
							),
						);

						if (targetIndex === -1) {
							return;
						}

						let referenceIndex = -1;
						if (referenceTrack) {
							referenceIndex = nodes.findIndex((node) =>
								isMatchingTrack(
									node,
									referenceTrack.mainSource,
									referenceTrack.trackType,
									referenceTrack.supportingSources || [],
								),
							);
						}

						const newNodes = [...nodes];
						const [movedTrack] = newNodes.splice(targetIndex, 1);

						const adjustedReferenceIndex =
							referenceIndex > targetIndex ? referenceIndex - 1 : referenceIndex;
						const insertPosition = referenceIndex === -1 ? 0 : adjustedReferenceIndex + 1;
						newNodes.splice(insertPosition, 0, movedTrack);

						tracksRecord.setLinkedRecords(newNodes, 'nodes');
					},
					onCompleted: (response) => {
						const success = response.confluence_reorderTracks?.success;
						const errors = response.confluence_reorderTracks?.errors;

						if (success) {
							resolve(true);
							if (flags && title) {
								flags.showFlag({
									title: formatMessage(messages.reorderSuccessTitle, { title }),
									type: 'success-circle',
									close: 'auto',
								});
							}
						} else {
							const errorMessage = errors?.[0]?.message || 'Failed to reorder track';
							reject(new Error(errorMessage));

							if (flags) {
								flags.showFlag({
									title: formatMessage(messages.errorTitle),
									description: formatMessage(messages.reorderErrorDescription),
									type: 'error',
									close: 'auto',
								});
							}
						}
					},
					onError: (error) => {
						reject(error);
						if (flags) {
							flags.showFlag({
								title: formatMessage(messages.errorTitle),
								description: formatMessage(messages.reorderErrorDescription),
								type: 'error',
								close: 'auto',
							});
						}
					},
				});
			});
		},
		[reorderTrackMutation, cloudId, flags, formatMessage],
	);

	return {
		removeTrack,
		isRemoving: isRemoveMutationInFlight,
		isDisabled: isRemoveMutationInFlight || isReorderMutationInFlight,
		addTrack,
		isAdding: isAddMutationInFlight,
		reorderTrack,
	};
};
